/**
 * `styleProps`: a free CSS value (any colour, a gradient, a custom property
 * computed at run time) on an element, set through the CSSOM after mount
 * (since 1.6.0).
 *
 * Why not Svelte's `style:` directive: in the browser it writes through the
 * CSSOM too, but when a page is **server-rendered** it serialises as a
 * `style="…"` attribute, which a strict Content Security Policy
 * (`style-src` without `'unsafe-inline'`) refuses. An action runs only in the
 * browser, after the element is in the document, and calls
 * `element.style.setProperty`, which no CSP blocks. The server sends the
 * element without the value; the stylesheet's own default (the accent, the
 * ink on it) shows until the page hydrates.
 *
 * The rule for the shell's parts (INTEGRATION.md § 7, "Server rendering"):
 * no `style:` directive and no `style` attribute. A value from a finite set is
 * a class; a free value goes through this action:
 *
 * ```svelte
 * <span class="ui-mark" use:styleProps={{ background: markBg, color: markInk }}></span>
 * ```
 *
 * An `undefined`, `null` or empty value sets nothing (and takes away what an
 * earlier value set), as `style:` does. Not exported from the package: it is
 * the shell's own tool (`test/ssr.test.ts` holds every part to the rule).
 */
import type { Action } from 'svelte/action';

/** Property names (`background`, `--ui-accent`) to values; empty ones are left unset. */
export type StyleProps = Readonly<Record<string, string | null | undefined>>;

export const styleProps: Action<HTMLElement, StyleProps> = (node, initial) => {
  let applied = new Set<string>();

  function apply(next: StyleProps): void {
    const names = new Set<string>();
    for (const [name, value] of Object.entries(next)) {
      if (value === undefined || value === null || value === '') continue;
      node.style.setProperty(name, value);
      names.add(name);
    }
    for (const name of applied) {
      if (!names.has(name)) node.style.removeProperty(name);
    }
    applied = names;
  }

  apply(initial);
  return { update: apply };
};
